import { buildCollection, buildProperty } from "@firecms/core";

import { ExportCollectionAction } from "@/actions/export_collection";

const googleActionEntityTypeEnumValues = {
  carecredit: "Care Credit",
  online_booking: "Online Booking",
  google_integration: "Google Integration",
  diamond_google: "Diamond Google",
} as const;

type GoogleActionEntityType = keyof typeof googleActionEntityTypeEnumValues;

type GoogleActionEntity = {
  created_at?: Date;
  name: string;
  salesforce_id: string;
  entity_type?: GoogleActionEntityType;
  enabled: boolean;
  appointment_connection: string;
  custom_url?: string;
  telephone?: string;
  url?: string;
  location: {
    latitude: number;
    longitude: number;
  };
  address: {
    country: string;
    locality: string;
    region: string;
    postal_code: string;
    street_address: string;
  };
  unstructured_address: string;
};

export const googleActionEntitiesCollection = buildCollection<Partial<GoogleActionEntity>>({
  id: "google_actions_entity",
  name: "Google Actions Entities",
  singularName: "Entity",
  textSearchEnabled: true,
  Actions: ExportCollectionAction,
  group: "Partners",
  path: "google_actions_entity",
  permissions: ({ authController }) => {
    const isAdmin = authController.extra?.roles.includes("admin") ?? false;
    return {
      edit: isAdmin,
      create: isAdmin,
      delete: isAdmin,
    };
  },
  properties: {
    created_at: buildProperty({
      name: "Created At",
      autoValue: "on_create",
      validation: { required: false },
      dataType: "date",
    }),
    name: buildProperty({
      name: "Name",
      validation: { required: true },
      dataType: "string",
    }),
    salesforce_id: buildProperty({
      name: "Salesforce ID",
      validation: { required: true },
      dataType: "string",
    }),
    entity_type: buildProperty({
      name: "Entity Type",
      validation: { required: false },
      dataType: "string",
      enumValues: googleActionEntityTypeEnumValues,
    }),
    appointment_connection: buildProperty({
      name: "Appointment Connection",
      validation: { required: false },
      dataType: "reference",
      path: "connections",
      previewProperties: ["name"],
    }),
    custom_url: buildProperty({
      name: "Custom URL",
      url: true,
      validation: { required: false },
      dataType: "string",
    }),
    enabled: buildProperty({
      name: "Enabled",
      validation: { required: true },
      dataType: "boolean",
      defaultValue: true,
    }),
    telephone: buildProperty({
      name: "Telephone",
      validation: { required: false },
      dataType: "string",
    }),
    url: buildProperty({
      name: "URL",
      url: true,
      validation: { required: false },
      dataType: "string",
    }),
    location: buildProperty({
      name: "Location",
      validation: { required: false },
      dataType: "map",
      properties: {
        latitude: buildProperty({
          name: "Latitude",
          validation: {
            required: false,
            min: -90,
            max: 90,
            matchesMessage: "Must be a valid latitude between -90 and 90",
          },
          dataType: "number",
        }),
        longitude: buildProperty({
          name: "Longitude",
          validation: {
            required: false,
            min: -180,
            max: 180,
            matchesMessage: "Must be a valid longitude between -180 and 180",
          },
          dataType: "number",
        }),
      },
    }),
    address: buildProperty({
      name: "Address",
      validation: { required: false },
      dataType: "map",
      properties: {
        country: buildProperty({
          name: "Country",
          validation: { required: false, length: 2, matchesMessage: "Must be a 2-letter ISO code" },
          dataType: "string",
        }),
        locality: buildProperty({
          name: "Locality",
          validation: { required: false },
          dataType: "string",
        }),
        region: buildProperty({
          name: "Region",
          validation: { required: false },
          dataType: "string",
        }),
        postal_code: buildProperty({
          name: "Postal Code",
          validation: { required: false },
          dataType: "string",
        }),
        street_address: buildProperty({
          name: "Street Address",
          validation: { required: false },
          dataType: "string",
        }),
      },
    }),
    unstructured_address: buildProperty({
      name: "Unstructured Address",
      validation: { required: false },
      dataType: "string",
      multiline: true,
      longText: true,
      longTextRows: 5,
    }),
  },
});
